import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Camera, Users, FileCheck, Check } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Field } from "./PropertyDetail";

const SELL_IMG = "https://images.pexels.com/photos/7031406/pexels-photo-7031406.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1200&dpr=2";

export default function Sell() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "valuation", type: "valuation" });
      setDone(true);
      toast.success(lang === "es" ? "¡Solicitud recibida!" : "Request received!");
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  const steps = [
    { icon: FileCheck, title: lang === "es" ? "Valoración Gratuita" : "Free Valuation", desc: lang === "es" ? "Analizamos el mercado para fijar el precio ideal." : "We analyze the market to price your home perfectly." },
    { icon: Camera, title: lang === "es" ? "Marketing Cinematográfico" : "Cinematic Marketing", desc: lang === "es" ? "Fotografía, video y tours virtuales de lujo." : "Luxury photography, video, and virtual tours." },
    { icon: Users, title: lang === "es" ? "Compradores Cualificados" : "Qualified Buyers", desc: lang === "es" ? "Acceso a nuestra red global de compradores." : "Access to our global network of buyers." },
    { icon: TrendingUp, title: lang === "es" ? "Cierre al Mejor Valor" : "Close at Top Value", desc: lang === "es" ? "Negociación experta para maximizar tu retorno." : "Expert negotiation to maximize your return." },
  ];

  return (
    <div className="pt-24">
      <section className="grid grid-cols-1 lg:grid-cols-2">
        <div className="bg-card/40 px-5 lg:px-16 py-20 flex flex-col justify-center">
          <span className="label-caps text-primary">{t("lifestyle.seller")}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-light mt-3 leading-tight">{lang === "es" ? "Descubre el valor de tu propiedad" : "Discover what your home is worth"}</h1>
          <p className="text-muted-foreground font-light mt-4 text-lg">{lang === "es" ? "Solicita una valoración gratuita y sin compromiso de uno de nuestros asesores de lujo." : "Request a free, no-obligation valuation from one of our luxury advisors."}</p>

          {done ? (
            <div className="mt-10 border border-primary/40 bg-primary/5 p-8 text-center" data-testid="valuation-success">
              <span className="w-14 h-14 rounded-full bg-primary text-primary-foreground grid place-items-center mx-auto mb-4"><Check size={28} /></span>
              <h3 className="font-serif text-2xl">{lang === "es" ? "¡Gracias!" : "Thank you!"}</h3>
              <p className="text-muted-foreground font-light mt-2">{lang === "es" ? "Un asesor te contactará en breve." : "An advisor will contact you shortly."}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-3 max-w-md" data-testid="valuation-form">
              <Field label={t("common.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="val-name" required />
              <Field label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="val-email" required />
              <Field label={t("common.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="val-phone" />
              <div>
                <label className="label-caps text-muted-foreground">{lang === "es" ? "Dirección de la propiedad" : "Property address"}</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} data-testid="val-message"
                  className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors resize-none" />
              </div>
              <button disabled={loading} className="w-full py-3.5 bg-primary text-primary-foreground label-caps disabled:opacity-50 hover:brightness-110 transition-[filter]" data-testid="val-submit">{loading ? t("common.sending") : (lang === "es" ? "Solicitar Valoración" : "Request Valuation")}</button>
            </form>
          )}
        </div>
        <div className="relative min-h-[400px] hidden lg:block">
          <img src={SELL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <h2 className="font-serif text-4xl font-light text-center mb-12">{lang === "es" ? "Cómo Trabajamos" : "How We Sell Your Home"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="border-t border-primary/40 pt-6">
              <span className="font-serif text-5xl text-primary/30">0{i + 1}</span>
              <s.icon size={26} className="text-primary my-3" strokeWidth={1.3} />
              <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground font-light">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
